import * as THREE from "three";
import { Mesh, MeshBasicMaterial, Shape, ShapeGeometry, Vector2, Vector3 } from "three";

import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

import LengthDimensionData from "../sceneObjectData/LengthDimensionData";
import { areVectorsEqualWithinTolerance } from "../../utils/geometryUtils";
import Constants from "../../constants";
import LineSegment3d from "../../math/linearAlgebra/lineSegment3d";
import LengthDimensionSubGeometryBase from "./LengthDimensionSubGeometryBase";
import { createHitLine } from "../../utils/annotationUtils";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";
import { getSceneObjectStyle, getSceneObjectStyleName, SceneObjectStyle, SceneObjectStyleEnum } from "../sceneObject/SceneObjectStyle";

export default class LengthDimensionSubGeometry2d extends LengthDimensionSubGeometryBase { 
    
    dimensionLineGeometry: LineGeometry;
    startExtensionLineGeometry: LineGeometry;
    endExtensionLineGeometry: LineGeometry;

    dimensionLine: LineSegments2;
    startExtensionLine!: LineSegments2;
    endExtensionLine!: LineSegments2;
    startTriangleMesh: Mesh;
    endTriangleMesh: Mesh;

    lineMaterial2 = LengthDimensionSubGeometry2d.getLineMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));
    meshMaterial = LengthDimensionSubGeometry2d.getMeshMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));

    private static lineMaterialCache = new WeakMap<SceneObjectStyle, LineMaterial>();
    private static meshMaterialCache = new WeakMap<SceneObjectStyle, MeshBasicMaterial>();

    private static getLineMaterial(style: SceneObjectStyle): LineMaterial {
        let mat = LengthDimensionSubGeometry2d.lineMaterialCache.get(style);
        if (!mat) {
            mat = new LineMaterial({
                color: style.color,
                linewidth: style.lineWidth,
                resolution: new Vector2(window.innerWidth, window.innerHeight),
                dashed: false,
                depthTest: false
            });
            mat.name = `LengthDim2d-${getSceneObjectStyleName(style)}-Line`;
            LengthDimensionSubGeometry2d.lineMaterialCache.set(style, mat);
        }
        return mat;
    }

    private static getMeshMaterial(style: SceneObjectStyle): MeshBasicMaterial {
        let mat = LengthDimensionSubGeometry2d.meshMaterialCache.get(style);
        if (!mat) {
            mat = new MeshBasicMaterial({ color: style.color, depthTest: false });
            mat.name = `LengthDim2d-${getSceneObjectStyleName(style)}-Mesh`;
            LengthDimensionSubGeometry2d.meshMaterialCache.set(style, mat);
        }
        return mat;
    }
    
    constructor() {
        super();
        
        // Build 2d triangles
        const triangleShape = this.buildTriangleShape();
        this.startTriangleMesh = new Mesh( new ShapeGeometry(triangleShape), this.meshMaterial);
        this.startTriangleMesh.name = "LengthDimensionSubGeometry2d.startTriangleMesh";
        this.startTriangleMesh.scale.set(   LengthDimensionSubGeometryBase.triangleScale, 
                                            LengthDimensionSubGeometryBase.triangleScale, 
                                            LengthDimensionSubGeometryBase.triangleScale); // Scale the triangle mesh to fit the line

        this.endTriangleMesh = this.startTriangleMesh.clone(); // Clone the start triangle mesh for the end triangle mesh
        this.endTriangleMesh.name = "LengthDimensionSubGeometry2d.endTriangleMesh";
        
        this.startExtensionLineGeometry = new LineGeometry();
        this.startExtensionLine = new LineSegments2(this.startExtensionLineGeometry, this.lineMaterial2);
        this.startExtensionLine.name = "LengthDimensionSubGeometry2d.startExtensionLine";

        this.endExtensionLineGeometry = new LineGeometry();
        this.endExtensionLine = new LineSegments2(this.endExtensionLineGeometry, this.lineMaterial2);
        this.endExtensionLine.name = "LengthDimensionSubGeometry2d.endExtensionLine";
        
        this.dimensionLineGeometry = new LineGeometry();
        this.dimensionLine = new LineSegments2(this.dimensionLineGeometry, this.lineMaterial2);
        this.dimensionLine.name = "LengthDimensionSubGeometry2d.dimensionLine";

        // Add parts to the group
        this.group.add(this.startExtensionLine); // Add the line to the group
        this.group.add(this.endExtensionLine); // Add the line to the group
        this.group.add(this.dimensionLine);

        // Invisible, wider hit targets alongside each visible line
        this.group.add(createHitLine(this.startExtensionLineGeometry));
        this.group.add(createHitLine(this.endExtensionLineGeometry));
        this.group.add(createHitLine(this.dimensionLineGeometry));

        this.group.add(this.startTriangleMesh); // Add the triangle mesh to the scene
        this.group.add(this.endTriangleMesh); // Add the triangle mesh to the scene

        this.group.add(this.textGroup);

        this.group.traverse((obj) => {
            obj.renderOrder = RenderOrderEnum.Annotation2d;
        });
        
        //this.textMaterial.depthTest = false; // Ensure text is always visible
    }

    buildTriangleShape(): Shape {
        const triangleShape = new Shape();
        const apex = Math.sqrt(3) / 2;
        triangleShape.moveTo(0.0, 0); 
        triangleShape.lineTo(-0.5, -apex); 
        triangleShape.lineTo(0.5, -apex)
        triangleShape.lineTo(0.0, 0); // Close the triangle
        return triangleShape;
    }

    // Update start and end triangle meshes
    updateEndMarkers(p1: Vector3, p2: Vector3) {
        const lineVector = new Vector3().subVectors(p1, p2);
        const startDir = lineVector.clone().normalize(); // Calculate the direction of the line
        const endDir = startDir.clone().negate(); // Calculate the direction of the line

        // Flip end markers to outside if line is too small
        if (lineVector.length() < this.endMarkerFlipDistance) {
            startDir.negate();
            endDir.negate();
        }

        LengthDimensionSubGeometryBase.updateEndMarker(this.startTriangleMesh, p1, startDir); 
        LengthDimensionSubGeometryBase.updateEndMarker(this.endTriangleMesh, p2, endDir); 
    }

    updateLineDimensions(newData: LengthDimensionData, forceUpdate: boolean): boolean {

        if (this.data && !forceUpdate
            && areVectorsEqualWithinTolerance(this.data.start, newData.start, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE) 
            && areVectorsEqualWithinTolerance(this.data.end, newData.end, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE)
            && areVectorsEqualWithinTolerance(this.data.offset, newData.offset, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE)) {

            return false; // if line dimensions are the same, no need to update
            }

        const dimensionLine = new LineSegment3d(    this.getOffsetPoint(newData.start, newData.offset),
                                                    this.getOffsetPoint(newData.end, newData.offset)); // Get the start point with offset
    
        this.dimensionLineGeometry.setPositions(dimensionLine.points().flatMap(pt => [pt.x, pt.y, pt.z]));
        this.dimensionLine.computeLineDistances();

        // Update markers in both ends of the dimension line
        this.updateEndMarkers(dimensionLine.p1, dimensionLine.p2); 

        // Update extension lines
        this.startExtensionLineGeometry.setPositions([newData.start, this.calculateExtensionLineEndPt(newData.start, dimensionLine.p1)]
                                        .flatMap(pt => [pt.x, pt.y, pt.z]));
        this.endExtensionLineGeometry.setPositions([newData.end, this.calculateExtensionLineEndPt(newData.end, dimensionLine.p2)]
                                        .flatMap(pt => [pt.x, pt.y, pt.z]));

        return true;
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;
        const lineMat = LengthDimensionSubGeometry2d.getLineMaterial(style);
        const meshMat = LengthDimensionSubGeometry2d.getMeshMaterial(style);
        this.dimensionLine.material = lineMat;
        this.startExtensionLine.material = lineMat;
        this.endExtensionLine.material = lineMat;
        this.startTriangleMesh.material = meshMat;
        this.endTriangleMesh.material = meshMat;
        super.setStyle(style);
    }
    
}